import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewedWeekService } from '../../core/services/viewed-week/viewed-week.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';
import { DemoService } from 'src/app/modules/auth/services/demo/demo.service';
import {
  HELP_SUGGESTION_PREFIX,
  demoSuggestions,
} from 'src/app/help/config/demo-suggestions';
import { ChatBlocksComponent } from '../components/chat-blocks/chat-blocks.component';
import { HelpFeedbackButtonsComponent } from '../components/feedback-buttons/feedback-buttons.component';
import { HelpMessageBubbleComponent } from '../components/message-bubble/message-bubble.component';
import { HelpSourceCardComponent } from '../components/source-card/source-card.component';
import { HelpSuggestedQuestionsComponent } from '../components/suggested-questions/suggested-questions.component';
import { HelpThinkingIndicatorComponent } from '../components/thinking-indicator/thinking-indicator.component';
import {
  ChatField,
  ChatbotProposal,
  TranscriptEntity,
  ChatbotProposalBatch,
  ConversationTurn,
  DemoAiQuota,
  HelpRelated,
  HelpSource,
} from '../models/help-chat.models';
import {
  PreviewI18n,
  ProposalPreview,
  buildProposalPreview,
} from '../util/proposal-preview';
import {
  TranscriptSegment,
  resolveTranscriptMarks,
} from '../util/transcript-entities';
import { EmployeeService } from 'src/app/modules/employee/services/employee.service';
import { DepartmentService } from 'src/app/modules/organization/services/department.service';
import { HelpChatService } from '../services/help-chat.service';
import { VoiceInputService } from '../services/voice-input.service';
import { HelpHubStateService } from '../help-hub/help-hub-state.service';
import { PageToursService } from '../page-tours/page-tours.service';
import { LanguageService } from 'src/app/core/services/language/language.service';
import { XdsButtonComponent } from 'src/app/shared/ui/button/button.component';
import { XdsChipComponent } from 'src/app/shared/ui/chip/chip.component';
import { XdsActionItemDirective } from 'src/app/shared/ui/action-item/action-item.directive';
import { XdsIconButtonComponent } from 'src/app/shared/ui/icon-button/icon-button.component';
import { XdsTextareaComponent } from 'src/app/shared/ui/textarea/textarea.component';
import { XdsBadgeComponent } from 'src/app/shared/ui/badge/badge.component';
import { XdsBannerComponent } from 'src/app/shared/ui/banner/banner.component';
import {
  REGISTRATION_ROUTE,
  leaveToLiveRegistration,
} from 'src/app/modules/auth/services/demo/registration-target';

// Kalenderwoche fuer die Kontextzeile (C3). Derselbe Plugin-Weg wie im Planer und in
// der Besetzungsstatistik — eine eigene Wochenrechnung waere die zweite Wahrheit.
dayjs.extend(isoWeek);

/**
 * Der Assistent-Reiter des Help-Hubs — Fragen, Antworten, Schreib-Vorschläge, Sprach-Eingabe.
 *
 * ⚠️ **Nur eingebettet.** Diese Komponente wird ausschliesslich von
 * `help-hub.component.ts` gerendert; einen eigenständigen MatDialog gibt es nicht mehr.
 * Bis 2026-09-10 trug sie dafür noch einen zweiten Modus (`@Input() embedded`) mit eigener
 * Kopfzeile, Schliessen-Knopf, Fokus-Falle und `MatDialogRef` — 90 Zeilen, die niemand
 * erreichen konnte. Das war nicht nur überflüssig, sondern irreführend: Jede Änderung am
 * Chat musste gegen einen Modus mitgedacht werden, den es nicht gibt, und der
 * Dialog-Zweig sah in der Abnahme wie ein Risiko aus, das eintreten könnte.
 *
 * Identität (Avatar, Name, Status, Kontingent-Chip, „Neue Unterhaltung") und der
 * Schliessen-Knopf gehören zum Hub-Kopf (`.hub-header`), nicht hierher — eine zweite
 * Kopfzeile sagte dasselbe ein zweites Mal und kostete 52,8 px (A2).
 */
@Component({
  selector: 'app-help-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    HelpMessageBubbleComponent,
    HelpSourceCardComponent,
    HelpSuggestedQuestionsComponent,
    HelpFeedbackButtonsComponent,
    HelpThinkingIndicatorComponent,
    ChatBlocksComponent,
    XdsButtonComponent,
    XdsChipComponent,
    // ⚠️ Attribut-Direktive: ohne Eintrag hier bleibt sie WIRKUNGSLOS — kein Fehler,
    // keine Warnung, nur eine Zeile ohne Kit-Optik und ohne Trefferziel.
    XdsActionItemDirective,
    XdsIconButtonComponent,
    XdsTextareaComponent,
    // Badge trägt die zwei Aufnahmearten im Sprach-Einstieg (C2) — als BESCHRIFTUNG,
    // nicht als Bedienelement; siehe Begründung im Template.
    XdsBadgeComponent,
    XdsBannerComponent,
    // ⚠️ Avatar und Tooltip standen hier bis 2026-09-10 — sie wurden ausschliesslich von
    // der entfernten Kopfzeile gebraucht. Sie im Bündel zu lassen wäre gratis gewesen,
    // aber ein Import ohne Verwendung liest sich später wie eine vergessene Fläche und
    // lädt dazu ein, eine zu bauen.
  ],
  templateUrl: './help-chat.component.html',
  styleUrls: ['./help-chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HelpChatComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('inputEl') private inputEl?: XdsTextareaComponent;
  @ViewChild('scrollEl') private scrollEl?: ElementRef<HTMLElement>;

  /** Emit bei Wunsch nach einer Schritt-fuer-Schritt-Anleitung (tourKey). Der Hub uebernimmt Preflight + Tour-Start. */
  @Output() requestGuide = new EventEmitter<string>();

  protected inputValue = '';

  /**
   * Persistente Schnellzugriffs-Leiste (Variante A „Signature Copilot"): bewusst nur
   * entitaetsfreie „Erledigen"-Beispiele (kein Namens-Interpolation-Bedarf wie in
   * HelpSuggestedQuestionsComponent) — bleibt waehrend der GESAMTEN Unterhaltung sichtbar,
   * anders als die ausfuehrliche Beispiel-Liste im Empty State.
   */
  protected readonly quickActionKeys: readonly string[] = [
    'HELP_CHAT.SUGGESTED.DO_AUTOFILL',
    'HELP_CHAT.SUGGESTED.DO_PUBLISH',
    'HELP_CHAT.SUGGESTED.DO_ADD_EMP',
  ];

  /**
   * Dieselbe Leiste in der Demo — mit den branchenspezifischen Beispielen.
   *
   * `DO_ADD_EMP` („Mitarbeiter anlegen") ist in einer Vorführung der falsche Schnellzugriff:
   * Die Demo-Firma ist bereits bestückt, und Einrichtungsarbeit ist genau das, was den Besuch
   * heute abbrechen lässt.
   */
  protected get visibleQuickActionKeys(): readonly string[] {
    if (!this.demoService.isDemoActive()) {
      return this.quickActionKeys;
    }
    return demoSuggestions(this.demoService.getIndustry()).do.map(
      (key) => HELP_SUGGESTION_PREFIX + key
    );
  }

  /**
   * C2 — Anzeigenamen aus dem eigenen Betrieb, gegen die das Transkript abgeglichen wird,
   * solange das Backend keine aufgeloesten Entitaeten liefert (Vertragspunkt 4).
   */
  private employeeNames: string[] = [];
  private departmentNames: string[] = [];

  private get knownNames(): string[] {
    return [...this.employeeNames, ...this.departmentNames];
  }

  /** Press-and-hold-State des Mikro-Buttons (WhatsApp-Stil). */
  private static readonly MIN_RECORD_MS = 400;
  private static readonly CANCEL_DRAG_PX = 90;
  private micPressActive = false;
  private recordStartedAt = 0;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private cancelOnRelease = false;

  constructor(
    public chat: HelpChatService,
    public voice: VoiceInputService,
    private router: Router,
    private translate: TranslateService,
    private viewedWeek: ViewedWeekService,
    private logger: ClientLoggerService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    private demoService: DemoService,
    private language: LanguageService,
    private employeeService: EmployeeService,
    private departmentService: DepartmentService,
    // ⚠️ `@Optional()` bleibt: Die Unit-Spec baut die Klasse ohne Injector, und der
    // Hub ist zwar der einzige Aufrufer, aber nicht die einzige Aufrufform.
    @Optional() private hubState: HelpHubStateService | null,
    // Review e: Tour der aktuellen Seite als Ausweg bei „nichts gefunden“. Optional aus
    // demselben Grund wie `hubState` (Unit-Spec ohne Injector).
    @Optional() private pageTours?: PageToursService | null,
  ) {}

  /**
   * Support-Ausweg bei „nichts gefunden“ (Review e). Dieselbe Adresse wie die 403-Seite
   * (`forbidden.component.ts`, seit Task #2 `.de` wie die Landingpage).
   */
  protected get supportMailto(): string {
    return 'mailto:support@fixplaner.de';
  }

  /**
   * Tour für den Ausweg „Tour öffnen“: die erste Tour der aktuellen Seite (dieselbe Liste wie
   * der Knopf „Tour“ im Seitenkopf). Ohne Tour auf der Seite entfällt der Knopf — einen
   * Knopf, der nichts startet, gibt es nicht.
   */
  protected noMatchTourId(): string | null {
    return this.pageTours?.tours()[0] ?? null;
  }

  /** Ausweg „Anders formulieren“: die letzte Frage zurück ins Feld, Fokus ans Ende. */
  protected onNoMatchRephrase(): void {
    const lastQuestion = [...this.chat.turns()].reverse().find((t) => t.role === 'user')?.content ?? '';
    this.onChipSelect(lastQuestion);
  }

  ngOnInit(): void {
    // Eine ueber HelpHubStateService.openTo() vorbefuellte Frage uebernehmen — der Weg
    // der Kontext-Einstiege (C4) und von field-help „Frag mich".
    if (this.hubState) {
      const pending = this.hubState.consumePendingQuestion();
      if (pending) {
        this.inputValue = pending;
      }
    }
    // Initialen Demo-KI-Kontingent-Stand holen (No-Op ausserhalb einer Demo-Session).
    this.chat.refreshDemoAiQuota();

    // C2 — Namensliste fuer die Markierung im Sprach-Entwurf. Beide Listen sind ohnehin
    // gecacht (der Empty-State liest sie seit jeher); hier kostet das keinen Aufruf mehr.
    this.employeeService.employeeNameList
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        this.employeeNames = (list ?? [])
          .map((e: { Name?: string }) => (e?.Name ?? '').trim())
          .filter((n: string) => n.length > 0);
      });
    this.departmentService.departementList
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        this.departmentNames = (list ?? [])
          .map((d: { Name?: string }) => (d?.Name ?? '').trim())
          .filter((n: string) => n.length > 0);
      });

    // B2: Die Vorschau-Felder sind übersetzt und gecacht. Ohne dieses Abonnement blieben
    // Beschriftungen wie „Zeitraum" nach einem Sprachwechsel auf Deutsch stehen, während
    // der Rest der Oberfläche schon Englisch ist.
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.previewCache.clear();
      this.cdr.markForCheck();
    });
  }

  /** Demo-KI-Kontingent-Stand (null ausserhalb einer Demo → Badge/Hinweis ausgeblendet). */
  protected get demoQuota(): DemoAiQuota | null {
    return this.chat.demoAiQuota();
  }

  /** Warn-Schwelle: nur noch wenige (>0) KI-Anfragen uebrig → dezenter Chat-Hinweis. */
  protected get quotaLow(): boolean {
    const q = this.demoQuota;
    return !!q && q.remaining > 0 && q.remaining <= 3;
  }

  // ⚠️ `quotaBadgeVariant` stand hier bis 2026-09-10 — es färbte das Kontingent-Badge der
  // entfernten Kopfzeile. Dasselbe Badge steht heute im Hub-Kopf, und der bringt seine
  // eigene, gleichnamige Methode mit (help-hub.component.ts). Zwei Kopien derselben
  // Schwellenlogik in einem Modul sind eine Quelle zu viel.

  /** Registrierung aus dem Kontingent-Hinweis (gleicher Pfad wie der demo-quota-dialog). */
  protected onRegisterFromQuota(): void {
    if (leaveToLiveRegistration()) {
      return;
    }
    this.router.navigateByUrl(REGISTRATION_ROUTE);
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      // Autofokus NUR auf Maus-Geraeten (Desktop). Auf Touch-Tablets/Smartphones wuerde
      // der Fokus sofort die Bildschirm-Tastatur aufklappen und den Chat verdecken —
      // obwohl der Nutzer evtl. nur per Mikrofon sprechen will. Dort erst fokussieren,
      // wenn er das Eingabefeld bewusst antippt.
      if (this.prefersAutofocus()) {
        this.inputEl?.focus();
      }
      // Auto-Grow auf die passende Hoehe uebernimmt xds-textarea selbst (writeValue +
      // eigenes ngAfterViewInit) fuer eine vorbefuellte Frage (initialQuestion / Hub-Pending).
      // Beim (Wieder-)Oeffnen ans Ende springen: eine bestehende Unterhaltung zeigt die
      // ZULETZT gestellte Frage/Antwort, nicht den Anfang oben (sonst muss der Nutzer jedes
      // Mal runterscrollen). Sofort (kein Smooth) -> kein sichtbarer Sprung beim Oeffnen.
      //
      // ⚠️ NUR mit Unterhaltung. Im leeren Zustand gibt es kein „zuletzt", und der Sprung
      // schob genau das aus dem Bild, was dort zuerst stehen soll: Überschrift, „Heute in
      // deinem Betrieb", der Sprechen-Einstieg. Am Gerät gemessen (390×844, 09.09.2026):
      // scrollTop stand nach dem Öffnen auf 64,8 — der Nutzer sah den Anfang nie und hätte
      // nach OBEN scrollen müssen, um den Einstieg zu finden.
      if (!this.chat.isEmpty()) {
        this.scrollToBottom(true);
      }
    });

    // Als Hub-Reiter bleibt der Chat beim Tab-Wechsel am Leben (nur [hidden]).
    // Wird der Assistent-Tab wieder sichtbar, erneut ans Ende springen — im versteckten
    // Zustand misst scrollHeight 0, daher ist der Scroll erst beim Reaktivieren wirksam.
    if (this.hubState) {
      this.hubState.activeTab$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tab) => {
        // Auch hier nur mit Unterhaltung — s. Begruendung oben.
        if (tab === 'fragen' && !this.chat.isEmpty()) this.scrollToBottom(true);
      });
      // A2: „Neue Unterhaltung" sitzt seit der Kopfzeilen-Zusammenlegung im Hub-Kopf.
      // Der Hub kennt weder Eingabewert noch Fokus — deshalb kommt nur das Signal von
      // dort, die Arbeit macht weiterhin diese Komponente.
      this.hubState.newConversation$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.newConversation();
          this.cdr.markForCheck();
        });
    }
  }

  /** true nur bei feinem Zeiger (Maus) — Touch-Geraete (Tablet/Smartphone) bekommen KEINEN Autofokus. */
  private prefersAutofocus(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  }

  /**
   * Klick auf einen Beispiel-Vorschlag: befuellt das Eingabefeld vor (statt sofort zu senden),
   * setzt Fokus + Caret ans Ende. Der Nutzer kann den Satz anpassen, abschicken oder per Mikro
   * einsprechen — die Beispiele sind Anregungen, kein fester Pfad.
   */
  protected onChipSelect(suggestion: string): void {
    this.inputValue = suggestion;
    this.cdr.markForCheck();
    queueMicrotask(() => {
      // xds-textarea passt seine Hoehe beim programmatischen Wert selbst an (writeValue);
      // hier nur noch Fokus + Cursor ans Ende.
      this.inputEl?.focus();
      this.inputEl?.setCaretToEnd();
    });
  }

  /** Klick auf einen Schnellzugriffs-Chip: gleiches Verhalten wie ein Beispiel-Chip (vorbefuellen, nicht auto-senden). */
  protected onQuickAction(translationKey: string): void {
    this.onChipSelect(this.translate.instant(translationKey));
  }

  // ===== C3 · @-Erwähnung und Kontextzeile =====

  /**
   * Auf der Eingabeseite gibt es genau ein grosses Problem, und es heisst **Namen**. Jede
   * Aktion braucht eine Person; Tippen ist auf 390px langsam, Sprache verhört Eigennamen.
   *
   * `@` plus drei Buchstaben öffnet die Liste der **existierenden** Personen und
   * Abteilungen. Die Abteilung steht als Unterscheidungsmerkmal daneben — genau die
   * Information, die „welche Kerstin" beantwortet.
   *
   * ⚠️ **Bewusst KEINE Schrägstrich-Befehle.** Am Schreibtisch das Standardmuster für
   * Fähigkeits-Entdeckung; auf einem mobilen Composer konkurriert das Menü mit der
   * Bildschirmtastatur um denselben Platz, und die meisten lernen die Syntax nie. Für
   * Entdeckung ist die Themen-Landkarte (C1) der bessere Weg.
   *
   * ⚠️ **Und bewusst eine Textarea, kein Baustein-Feld.** Ein Feld mit Bausteinen ist kein
   * `<textarea>` mehr — Auto-Grow, Zwischenablage, Cursor-Verhalten und die Sprachaufnahme
   * greifen heute alle darauf zu. Der eingesetzte Name bleibt deshalb Text; gewonnen ist,
   * dass es ein Name aus dem eigenen Betrieb ist.
   */
  private static readonly MENTION_MIN_CHARS = 2;
  private static readonly MENTION_MAX_ROWS = 5;

  /** Offene Treffer der Erwähnungsliste (leer = Liste zu). */
  protected mentionMatches: { name: string; meta?: string }[] = [];
  protected mentionQuery = '';
  protected mentionKind: 'employee' | 'department' = 'employee';
  /** Zeichenbereich des `@…`-Fragments im Eingabefeld. */
  private mentionRange: { start: number; end: number } | null = null;

  protected onInputChanged(value: string): void {
    this.inputValue = value ?? '';
    this.updateMentions();
  }

  private updateMentions(): void {
    const text = this.inputValue;
    // Letztes `@wort` am ENDE des Textes — nur dort tippt man gerade.
    const m = /(^|\s)@([\p{L}\p{N}\-.]*)$/u.exec(text);
    if (!m) {
      this.mentionMatches = [];
      this.mentionRange = null;
      return;
    }
    const query = m[2];
    if (query.length < HelpChatComponent.MENTION_MIN_CHARS) {
      this.mentionMatches = [];
      this.mentionRange = null;
      return;
    }
    const start = text.length - query.length - 1; // inklusive des „@"
    this.mentionRange = { start, end: text.length };
    this.mentionQuery = query;

    const needle = query.toLowerCase();
    const treffer = this.employeeNames
      .filter((n) => n.toLowerCase().includes(needle))
      .map((name) => ({ name, meta: undefined as string | undefined }));
    const abteilungen = this.departmentNames
      .filter((n) => n.toLowerCase().includes(needle))
      .map((name) => ({ name, meta: undefined as string | undefined }));

    this.mentionKind = treffer.length ? 'employee' : 'department';
    this.mentionMatches = (treffer.length ? treffer : abteilungen).slice(
      0,
      HelpChatComponent.MENTION_MAX_ROWS,
    );
  }

  /** Auswahl einsetzen: `@ker` wird zum vollen, existierenden Namen. */
  protected applyMention(match: { name: string }): void {
    if (!this.mentionRange) return;
    const { start, end } = this.mentionRange;
    this.inputValue = `${this.inputValue.slice(0, start)}${match.name} ${this.inputValue.slice(end)}`;
    this.mentionMatches = [];
    this.mentionRange = null;
    this.cdr.markForCheck();
    queueMicrotask(() => {
      this.inputEl?.focus();
      this.inputEl?.setCaretToEnd();
    });
  }

  /** Initialen für die Zeile — zwei Buchstaben, wie überall in der App. */
  protected initialsOf(name: string): string {
    return (name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((t) => t[0]?.toUpperCase() ?? '')
      .join('');
  }

  /**
   * „KW 38" — der Zeitbezug, auf den sich „nächste Woche" bezieht.
   *
   * Der Nutzer rät heute, worauf sich eine Zeitangabe bezieht — und bekam bei der
   * Fairness-Frage stillschweigend das ganze Jahr. Die Zeile macht den Bezug sichtbar.
   * (Dass die Antwort ihn auch EINHÄLT, ist Backend-Arbeit, Punkt 8 der Anpassungsliste.)
   */
  protected get currentWeekLabel(): string {
    // Die Woche, die die Seite ANZEIGT (Planer), sonst heute — siehe ViewedWeekService.
    return this.translate.instant('HELP_CHAT.CONTEXT_WEEK', { week: this.viewedWeek.isoWeek() });
  }

  protected onSubmit(): void {
    if (!this.inputValue.trim()) return;
    this.mentionMatches = [];
    this.send(this.inputValue.trim());
  }

  protected onInputKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      this.onSubmit();
    }
  }

  // ===== Sprach-Eingabe (WhatsApp-Stil: halten -> aufnehmen, loslassen -> senden) =====

  /** Mikro gedrueckt: Aufnahme starten. Pointer-Capture, damit pointerup zuverlaessig hier ankommt. */
  protected async onMicDown(ev: PointerEvent): Promise<void> {
    ev.preventDefault();
    // C2 — Zweiter Aufnahmemodus: Läuft eine Freihand-Aufnahme, beendet dieser Tipp sie.
    // Halten ermüdet bei langen Sätzen, Freihand vermeidet versehentliche Aufnahmen —
    // deshalb beides, mit derselben Taste und einer klaren Stopp-Bedingung.
    if (this.handsFree) {
      this.stopHandsFree();
      return;
    }
    if (this.chat.pending() || this.voice.state() !== 'idle') return;
    (ev.target as HTMLElement)?.setPointerCapture?.(ev.pointerId);
    this.micPressActive = true;
    this.cancelOnRelease = false;
    this.pointerStartX = ev.clientX;
    this.pointerStartY = ev.clientY;

    const started = await this.voice.startRecording();
    if (!started) {
      this.micPressActive = false;
      return;
    }
    this.recordStartedAt = Date.now();
    // User hat schon losgelassen, bevor die Permission aufgeloest war -> sofort verwerfen.
    if (!this.micPressActive) this.voice.cancel();
  }

  /** Wegziehen waehrend des Haltens markiert „abbrechen" (Slide-to-cancel). */
  protected onMicMove(ev: PointerEvent): void {
    if (!this.micPressActive || this.voice.state() !== 'recording') return;
    const dist = Math.hypot(ev.clientX - this.pointerStartX, ev.clientY - this.pointerStartY);
    if (dist > HelpChatComponent.CANCEL_DRAG_PX) this.cancelOnRelease = true;
  }

  /** Mikro losgelassen: stoppen, transkribieren und Ergebnis direkt an die KI senden. */
  protected async onMicUp(ev: PointerEvent): Promise<void> {
    if (!this.micPressActive) return;
    this.micPressActive = false;
    ev.preventDefault?.();
    if (this.voice.state() !== 'recording') return;

    if (this.cancelOnRelease) {
      this.voice.cancel();
      return;
    }

    // Kurzer Tipp = Freihand. Bis 2026-09-09 wurde die Aufnahme hier verworfen und ein
    // Hinweis „Halten zum Aufnehmen" gezeigt — der stand in einem `title`, das auf Touch
    // nie erscheint. Statt einer Belehrung jetzt der zweite Modus: die Aufnahme LÄUFT
    // weiter, ein zweiter Tipp beendet sie.
    if (Date.now() - this.recordStartedAt < HelpChatComponent.MIN_RECORD_MS) {
      this.startHandsFree();
      return;
    }

    await this.stopAndTranscribe();
  }

  /**
   * C2 — Aufnahme beenden, transkribieren und als **Entwurf** zeigen statt zu senden.
   *
   * Bis 2026-09-09 ging das Transkript hier direkt an die KI („Auto-Send"). Der Nutzer sah
   * nie, was verstanden wurde — bei Eigennamen die anfälligste Stelle überhaupt, und vor
   * einer Schreibaktion die teuerste.
   */
  private async stopAndTranscribe(): Promise<void> {
    const audio = await this.voice.stopRecording();
    if (!audio) {
      this.voice.finish();
      return;
    }

    this.chat.transcribe(audio.blob, audio.fileName).subscribe({
      next: (result) => {
        this.voice.finish();
        const clean = (result?.text ?? '').trim();
        if (clean) {
          this.openVoiceDraft(clean, result.entities ?? []);
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.voice.finish();
        this.logger.logWarning('Help.TranscribeFailed', LogCategory.User, { error: String(err) });
        this.cdr.markForCheck();
      },
    });
  }

  // ===== C2 · Sprach-Entwurf =====

  /**
   * Sekunden bis zum automatischen Senden.
   *
   * Der Countdown ist der Kompromiss zwischen den zwei Wegen: Korrektur per Sprache ist
   * genau, aber langsam; automatisch zu senden ist schnell, aber ungenau. Drei Sekunden
   * reichen, um „falsch" zu erkennen, und halten den Geschwindigkeitsvorteil.
   *
   * ⚠️ Er MUSS abbrechbar sein — sonst ist er nur eine Verzögerung, und Sprache verliert
   * ihren einzigen Vorteil. Jeder der drei Knöpfe bricht ihn ab.
   */
  private static readonly DRAFT_COUNTDOWN_S = 3;

  /** Der aktuelle Entwurf, oder `null`. */
  protected voiceDraft: {
    text: string;
    segments: TranscriptSegment[];
    secondsLeft: number;
  } | null = null;

  private draftTimer: ReturnType<typeof setInterval> | null = null;

  private openVoiceDraft(text: string, entities: TranscriptEntity[]): void {
    this.clearDraftTimer();
    this.voiceDraft = {
      text,
      // Backend-Entitäten gewinnen; ohne sie der Abgleich gegen die gecachte Namensliste.
      segments: resolveTranscriptMarks(text, entities, this.knownNames),
      secondsLeft: HelpChatComponent.DRAFT_COUNTDOWN_S,
    };
    this.draftTimer = setInterval(() => {
      if (!this.voiceDraft) return;
      this.voiceDraft = { ...this.voiceDraft, secondsLeft: this.voiceDraft.secondsLeft - 1 };
      if (this.voiceDraft.secondsLeft <= 0) {
        this.sendVoiceDraft();
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  /** „Jetzt senden" — und dasselbe, wenn der Countdown abläuft. */
  protected sendVoiceDraft(): void {
    const text = this.voiceDraft?.text?.trim();
    this.closeVoiceDraft();
    if (text) this.send(text);
  }

  /** „Ändern" — der Text landet im Eingabefeld, nichts geht raus. */
  protected editVoiceDraft(): void {
    const text = this.voiceDraft?.text ?? '';
    this.closeVoiceDraft();
    this.onChipSelect(text);
  }

  /** „Verwerfen" — in jedem Zustand erreichbar. */
  protected closeVoiceDraft(): void {
    this.clearDraftTimer();
    this.voiceDraft = null;
    this.cdr.markForCheck();
  }

  private clearDraftTimer(): void {
    if (this.draftTimer) {
      clearInterval(this.draftTimer);
      this.draftTimer = null;
    }
  }

  /** Systemseitiger Abbruch (z.B. Anruf) -> Aufnahme verwerfen. */
  protected onMicCancel(): void {
    // Im Freihand-Modus ist `pointercancel` normal (der Finger ist längst weg) und darf
    // die laufende Aufnahme NICHT abbrechen — sonst endet jede Freihand-Aufnahme sofort.
    if (this.handsFree) return;
    this.micPressActive = false;
    this.cancelOnRelease = true;
    this.voice.cancel();
  }

  // ===== C2 · Freihand-Aufnahme =====

  /**
   * Höchstdauer einer Freihand-Aufnahme.
   *
   * Ohne sie liefe die Aufnahme weiter, wenn der Nutzer das Panel verlässt oder es
   * schlicht vergisst — das Mikrofon bliebe offen. Eine Freihand-Aufnahme braucht eine
   * eigene Stopp-Bedingung, und die Zeit ist die einzige, die immer greift.
   */
  private static readonly HANDSFREE_MAX_MS = 60_000;

  /** true, solange eine Freihand-Aufnahme läuft (Template zeigt „Stopp"). */
  protected handsFree = false;
  private handsFreeTimer: ReturnType<typeof setTimeout> | null = null;

  private startHandsFree(): void {
    this.handsFree = true;
    this.handsFreeTimer = setTimeout(
      () => this.stopHandsFree(),
      HelpChatComponent.HANDSFREE_MAX_MS,
    );
    this.cdr.markForCheck();
  }

  private stopHandsFree(): void {
    if (this.handsFreeTimer) {
      clearTimeout(this.handsFreeTimer);
      this.handsFreeTimer = null;
    }
    this.handsFree = false;
    this.cdr.markForCheck();
    void this.stopAndTranscribe();
  }

  /** Sprechen-Einstieg im leeren Zustand: startet die Freihand-Aufnahme. */
  protected async onVoiceCta(): Promise<void> {
    if (this.chat.pending() || this.voice.state() !== 'idle') return;
    const started = await this.voice.startRecording();
    if (!started) return;
    this.startHandsFree();
  }

  protected onLinkClick(href: string): void {
    if (!href) return;
    if (href.startsWith('tour:')) {
      // Preflight + Tour-Start uebernimmt der Hub (er kennt die Settings-Voraussetzungen),
      // der Chat bleibt dabei erreichbar.
      this.requestGuide.emit(href.slice(5).trim());
      return;
    }
    if (href.startsWith('app:')) {
      this.router.navigateByUrl(href.slice(4));
      return;
    }
    if (href.startsWith('/')) {
      this.router.navigateByUrl(href);
      return;
    }
    if (href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank');
      return;
    }
    if (href.startsWith('#')) {
      const id = href.slice(1);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    this.logger.logWarning('Help.UnknownLinkScheme', LogCategory.User, { href });
  }

  protected onSourceActivate(source: HelpSource): void {
    if (source.tourKey) {
      this.onLinkClick(`tour:${source.tourKey}`);
    }
  }

  /**
   * "Siehe auch"-Klick: stellt den Titel des verwandten Artikels als neue Frage.
   * Wiederverwendung des Ask-Pfads (kein separater GetBySlug-Endpoint) — der Titel
   * ist ein eingebetteter Titel, trifft den Artikel also mit ~0 Distanz.
   */
  protected onRelatedClick(related: HelpRelated): void {
    if (related?.title) {
      this.send(related.title);
    }
  }

  /**
   * Klick auf eine interaktive Karten-Zeile (chat-blocks): formt eine Folgefrage und schickt sie
   * ueber den normalen Ask-Pfad — so wird der Chat handlungsfaehig (z. B. Mitarbeiter → Profil).
   */
  protected onRowAction(ev: { action: string; arg: string }): void {
    const arg = (ev.arg ?? '').trim();
    if (!arg) return;
    // E1 — Die beiden Folgefragen standen als deutsche Literale im TS und gingen so auch
    // in einer englischen Oberfläche an die KI. Beide Wächter sind hier blind: der
    // Template-Wächter sieht nur `.html`, und `locale-and-text-sources` erkennt ein
    // Literal in einer Senke — `send()` ist eine, weshalb genau diese Zeilen im Bericht
    // als Fallklasse stehen. Jetzt aus de.json/en.json.
    // Rueckfrage-Auswahl (Blocktyp `choice`): Die Antwort auf „Welche Kerstin meinst du?"
    // ist der Name selbst — kein Satz drumherum. Ein „Ich meine Kerstin Below" waere in
    // sieben Sprachen zu pflegen und traegt keine zusaetzliche Information.
    if (ev.action === 'disambiguate') {
      this.send(arg);
      return;
    }
    const key =
      ev.action === 'profile'
        ? 'HELP_CHAT.ROW_ACTION.PROFILE'
        : ev.action === 'open_shift'
          ? 'HELP_CHAT.ROW_ACTION.OPEN_SHIFT'
          : null;
    if (!key) return;
    this.send(this.translate.instant(key, { name: arg, shift: arg }));
  }

  /** tourKey der ersten Quelle mit hinterlegter Tour (treibt die "Schritt-fuer-Schritt zeigen"-Affordanz). */
  protected guideTourKey(turn: ConversationTurn): string | undefined {
    if (turn.role !== 'assistant') return undefined;
    return turn.sources?.find((s) => s.tourKey)?.tourKey;
  }

  protected onRequestGuide(tourKey: string): void {
    this.onLinkClick(`tour:${tourKey}`);
  }

  protected onFeedback(payload: { feedbackId: string; helpful: boolean }): void {
    this.chat.submitFeedback(payload.feedbackId, payload.helpful).subscribe({
      error: (err) =>
        this.logger.logWarning('Help.FeedbackSubmitFailed', LogCategory.User, { error: String(err) }),
    });
  }

  /**
   * B2 — Anzeigemodell einer Vorschlagskarte, gecacht je `executionId`.
   *
   * ⚠️ Der Cache ist nicht Feintuning, sondern Voraussetzung: Das Template ruft
   * `previewFor()` in jedem Änderungserkennungs-Lauf mehrfach auf (Titel, Feld-Liste,
   * `*ngIf`). Ohne Cache entstünde jedes Mal ein NEUES Array — `*ngFor` würde die
   * gesamte Feldliste bei jedem Lauf neu aufbauen, und in Verbindung mit OnPush ist
   * genau das der bekannte „Getter liefert neue Referenz"-Fehler.
   *
   * Bei Sprachwechsel wird der Cache verworfen (siehe Konstruktor-Abonnement), sonst
   * blieben die Feldbeschriftungen in der alten Sprache stehen.
   */
  private readonly previewCache = new Map<string, ProposalPreview>();

  protected previewFor(proposal: ChatbotProposal): ProposalPreview {
    const cached = this.previewCache.get(proposal.executionId);
    if (cached) return cached;
    const preview = buildProposalPreview(proposal, this.previewI18n, this.language.getCurrentLanguage());
    this.previewCache.set(proposal.executionId, preview);
    return preview;
  }

  /**
   * Zugriff auf die Übersetzungen für das Anzeigemodell.
   *
   * `has()` ist der tragende Teil: `instant()` gibt bei einem unbekannten Schlüssel den
   * Schlüssel ZURÜCK. Ohne diese Prüfung stünde „HELP_CHAT.FIELD.strategyId" als
   * Feldbeschriftung auf einer Bestätigungskarte.
   */
  private readonly previewI18n: PreviewI18n = {
    instant: (key, params) => this.translate.instant(key, params),
    has: (key) => this.translate.instant(key) !== key,
  };

  /**
   * Titel des ausgeführten Werkzeugs für die Ergebniskarte — derselbe Schlüssel wie in der
   * Vorschau, damit „Plan automatisch füllen" vorher und nachher gleich heisst.
   *
   * Ohne gepflegten Titel bleibt das Feld leer und die Überschrift sagt nur „Erledigt";
   * ein roher Werkzeugname (`auto_fill_shifts`) gehört nicht auf den Bildschirm.
   */
  protected executedToolTitle(toolName: string): string {
    const key = `HELP_CHAT.TOOL.${toolName}`;
    return this.previewI18n.has(key) ? this.translate.instant(key) : '';
  }

  /** Feldliste ist stabil je Beschriftung — verhindert unnötiges Neu-Rendern. */
  protected trackByFieldLabel(_index: number, field: ChatField): string {
    return field.label;
  }

  /**
   * „Im Plan ansehen" nach einer ausgeführten Schreibaktion — der Weg aus dem Chat
   * zurück in die Arbeit. Das Hub-Panel schliesst dabei, sonst läge es über dem Plan,
   * den man sehen will.
   */
  protected onOpenPlanner(): void {
    this.router.navigateByUrl('/planner');
    this.hubState?.close();
  }

  /** Bestaetigt (true) oder verwirft (false) einen Schreib-Vorschlag des Assistenten. */
  protected onConfirmProposal(proposal: ChatbotProposal, confirmed: boolean): void {
    // Getaetigte Wahl als eigene User-Bubble zeigen (Nachvollziehbarkeit des Dialogs).
    const choiceLabel = this.translate.instant(
      confirmed ? 'HELP_CHAT.CHOICE_CONFIRMED' : 'HELP_CHAT.CHOICE_CANCELLED',
    );
    this.cdr.markForCheck();
    setTimeout(() => this.scrollToBottom());
    this.chat.confirm(proposal.executionId, confirmed, choiceLabel).subscribe({
      next: () => {
        this.cdr.markForCheck();
        queueMicrotask(() => this.scrollToBottom());
      },
      error: (err) =>
        this.logger.logWarning('Chatbot.ConfirmFailed', LogCategory.User, { error: String(err) }),
    });
  }

  /**
   * Bestaetigt (true) oder verwirft (false) eine Sammel-Bestaetigung (mehrere Aktionen).
   * Nutzt denselben Confirm-Pfad — die BatchId steckt im selben executionId-Feld.
   */
  protected onConfirmBatch(batch: ChatbotProposalBatch, confirmed: boolean): void {
    const choiceLabel = this.translate.instant(
      confirmed ? 'HELP_CHAT.CHOICE_CONFIRMED' : 'HELP_CHAT.CHOICE_CANCELLED',
    );
    this.cdr.markForCheck();
    setTimeout(() => this.scrollToBottom());
    this.chat.confirm(batch.executionId, confirmed, choiceLabel).subscribe({
      next: () => {
        this.cdr.markForCheck();
        queueMicrotask(() => this.scrollToBottom());
      },
      error: (err) =>
        this.logger.logWarning('Chatbot.ConfirmFailed', LogCategory.User, { error: String(err) }),
    });
  }

  protected newConversation(): void {
    this.chat.reset();
    this.inputValue = '';
    queueMicrotask(() => this.inputEl?.focus());
  }

  protected send(question: string): void {
    this.inputValue = '';
    // OnPush: die synchronen Signal-Mutationen in chat.ask() (User-Turn + pending)
    // sowie das geleerte Eingabefeld sofort flushen, sonst feuert die View erst beim
    // naechsten Event -> gefuehlter Doppelklick auf Quick-Action-Chips.
    this.cdr.markForCheck();
    // WARUM (2026-05-29): Sofort ans Ende scrollen, sobald die Frage abgeschickt ist.
    // Der User soll die gerade gestellte Frage + den Lade-Indikator direkt sehen, auch
    // wenn er vorher weiter oben in der Historie gescrollt war (Antwort kommt erst ~5 s
    // spaeter -> zweiter scrollToBottom im next-Callback). setTimeout statt queueMicrotask,
    // weil der neue User-Turn erst nach dem OnPush-Change-Detection-Lauf im DOM steht
    // (sonst ist scrollHeight noch der alte Wert).
    setTimeout(() => this.scrollToBottom());
    // E1 — Hier stand `currentLang === 'de' ? 'de' : 'de'`: ein Ausdruck, der in JEDEM Fall
    // 'de' liefert. Die App führt sieben Sprachen; ein englischsprachiger Nutzer bekam
    // stillschweigend deutsche Antworten, und die Stelle sah aus wie eine Fallunterscheidung.
    // Quelle der Sprache ist der LanguageService (kanonischer Short-Code), nie
    // `navigator.language` und nie `translate.currentLang` direkt — letzterer kann vor dem
    // Bootstrap noch undefined sein.
    this.chat.ask(question, this.language.getCurrentLanguage()).subscribe({
      next: () => {
        this.cdr.markForCheck();
        queueMicrotask(() => this.scrollToBottom());
      },
      error: (err) => {
        this.logger.logWarning('Help.AskFailed', LogCategory.User, { error: String(err) });
      },
    });
  }

  /**
   * Scrollt das Nachrichten-Panel sanft ans Ende. Smooth statt hartem Sprung,
   * damit der User die neue Antwort (bzw. die Bestaetigen/Abbrechen-Karte) ins
   * Sichtfeld wandern sieht und direkt reagieren kann.
   *
   * WARUM doppeltes requestAnimationFrame: der Aufruf kommt teils direkt nach
   * markForCheck(), bevor die frisch gerenderte Bubble vermessen ist — ein
   * einzelner Tick liefert dann noch den alten scrollHeight und es bleibt oben
   * haengen. Erst nach Layout (2. Frame) ist die Hoehe korrekt.
   */
  /**
   * Timer abraeumen.
   *
   * ⚠️ Ohne das liefe der Countdown des Sprach-Entwurfs nach dem Schliessen weiter und
   * schickte den Satz an die KI — eine Nachricht, die niemand abgeschickt hat.
   */
  ngOnDestroy(): void {
    this.clearDraftTimer();
    if (this.handsFreeTimer) clearTimeout(this.handsFreeTimer);
  }

  private scrollToBottom(instant = false): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = this.scrollEl?.nativeElement;
        if (!el) return;
        try {
          // instant=true beim (Wieder-)Oeffnen/Tab-Wechsel: sofort ans Ende ohne sichtbares
          // Scrollen. Sonst (nach Senden/Antwort) sanft, damit der Nutzer die Bewegung sieht.
          el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'auto' : 'smooth' });
        } catch {
          // Fallback fuer Umgebungen ohne Smooth-Scroll-Support.
          el.scrollTop = el.scrollHeight;
        }
      });
    });
  }
}
